import {
    currencyFormat,
    formatCentsToDisplay,
    parseCurrencyToCents,
} from '@/lib/form-state/money'
import { OnboardingCost } from '@/lib/api/onboarding/onboarding-api'
import { MdAdd, MdClose } from 'react-icons/md'
import { COMMON_COST_SUGGESTIONS } from '@/lib/pricing/service-cost'
import { costPerConsultation, totalCosts } from '../onboarding-state'

interface Props {
    value: OnboardingCost[]
    /** Consultas num mês típico — divide o custo mensal. */
    monthlyConsultations: number
    onChange: (costs: OnboardingCost[]) => void
    onChangeMonthlyConsultations: (quantity: number) => void
}

// As sugestões vivem em `lib/pricing/service-cost`, junto da fórmula: a mesma
// lista aparece na calculadora de custo do catálogo.

/**
 * Lista dinâmica de custos MENSAIS mais quantas consultas o médico faz no mês. O
 * custo de uma consulta é o rateio dos dois — é ele que vira o preço de custo no
 * catálogo, e dele saem markup e margem.
 *
 * Os custos NÃO viram lançamento no financeiro: ficam registrados no cadastro da
 * conta, e o médico pode revisá-los depois na tela de catálogo.
 */
export default function CostsStep({
    value,
    monthlyConsultations,
    onChange,
    onChangeMonthlyConsultations,
}: Props) {
    const total = totalCosts(value)
    const perConsultation = costPerConsultation(value, monthlyConsultations)

    const add = (description = '') => {
        onChange([...value, { description, amount: 0 }])
    }

    const remove = (index: number) => {
        onChange(value.filter((_, i) => i !== index))
    }

    const setField = (index: number, field: keyof OnboardingCost, raw: string) => {
        onChange(
            value.map((cost, i) => {
                if (i !== index) return cost
                if (field === 'description') return { ...cost, description: raw }
                return { ...cost, amount: parseCurrencyToCents(currencyFormat(raw, true)) }
            })
        )
    }

    const usedDescriptions = value.map(cost => cost.description.trim().toLowerCase())

    return (
        <div className="flex flex-col gap-5">
            <p className="text-base-content/70">
                Liste o que você gasta por mês para atender. Dividimos pelo número de
                consultas do mês para chegar ao custo de cada uma — é esse valor que vira
                o custo no catálogo e mostra a sua margem depois.
            </p>

            <div className="flex flex-wrap gap-2">
                {COMMON_COST_SUGGESTIONS.filter(
                    suggestion => !usedDescriptions.includes(suggestion.toLowerCase())
                ).map(suggestion => (
                    <button
                        key={suggestion}
                        type="button"
                        className="btn btn-xs btn-outline"
                        onClick={() => add(suggestion)}
                    >
                        <MdAdd className="text-sm" />
                        {suggestion}
                    </button>
                ))}
            </div>

            <ul className="flex flex-col gap-3">
                {value.map((cost, index) => (
                    <li key={index} className="flex items-end gap-3">
                        <div className="form-control flex-1">
                            <label className="label pb-1" htmlFor={`onboarding-cost-${index}`}>
                                <span className="label-text font-medium">
                                    Custo
                                    <span className="text-error ml-1">*</span>
                                </span>
                            </label>
                            <input
                                id={`onboarding-cost-${index}`}
                                className="input input-bordered w-full"
                                value={cost.description}
                                onChange={e => setField(index, 'description', e.target.value)}
                                placeholder="Aluguel da sala"
                            />
                        </div>

                        <div className="form-control w-44">
                            <label
                                className="label pb-1"
                                htmlFor={`onboarding-cost-amount-${index}`}
                            >
                                <span className="label-text font-medium">
                                    Valor
                                    <span className="text-error ml-1">*</span>
                                </span>
                            </label>
                            <input
                                id={`onboarding-cost-amount-${index}`}
                                className="input input-bordered w-full"
                                value={formatCentsToDisplay(cost.amount)}
                                onChange={e => setField(index, 'amount', e.target.value)}
                                placeholder="R$ 0,00"
                            />
                        </div>

                        <button
                            type="button"
                            className="btn btn-ghost btn-circle mb-1"
                            onClick={() => remove(index)}
                            aria-label="Remover custo"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    </li>
                ))}
            </ul>

            <button
                type="button"
                className="btn btn-outline btn-sm gap-1 self-start"
                onClick={() => add()}
            >
                <MdAdd className="text-lg" />
                Adicionar custo
            </button>

            <div className="rounded-lg border border-base-300 p-4 flex flex-col gap-4">
                <div className="form-control w-full sm:max-w-xs">
                    <label className="label pb-1" htmlFor="onboarding-monthly-consultations">
                        <span className="label-text font-medium">
                            Em média, quantas consultas você faz por mês?
                            <span className="text-error ml-1">*</span>
                        </span>
                    </label>
                    <input
                        id="onboarding-monthly-consultations"
                        className="input input-bordered w-full"
                        value={monthlyConsultations || ''}
                        onChange={e => {
                            const parsed = parseInt(e.target.value.replace(/\D/g, ''), 10)
                            onChangeMonthlyConsultations(
                                Number.isFinite(parsed) ? parsed : 0
                            )
                        }}
                        placeholder="80"
                        inputMode="numeric"
                    />
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                    <div className="min-w-0">
                        <p className="text-sm text-base-content/60">Custo mensal</p>
                        <p className="text-lg font-semibold text-base-content">
                            {formatCentsToDisplay(total) || 'R$ 0,00'}
                        </p>
                    </div>

                    <div className="min-w-0">
                        <p className="text-sm text-base-content/60">Custo por consulta</p>
                        <p className="text-xl font-semibold text-base-content">
                            {formatCentsToDisplay(perConsultation) || 'R$ 0,00'}
                        </p>
                    </div>
                </div>
            </div>

            {total > 0 && monthlyConsultations <= 0 && (
                <div className="alert alert-warning text-sm">
                    <span className="whitespace-normal">
                        Informe quantas consultas você faz por mês. Sem isso, não dá para
                        saber quanto custa uma consulta — e o custo mensal inteiro viraria o
                        custo de cada atendimento.
                    </span>
                </div>
            )}

            {value.length === 0 && (
                <div className="alert alert-info text-sm">
                    <span className="whitespace-normal">
                        Informe ao menos um custo. Sem ele a consulta nasceria só com o
                        valor cobrado, e o sistema não teria como calcular a sua margem.
                    </span>
                </div>
            )}
        </div>
    )
}
